import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  Paper,
  IconButton,
  Fab,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FilterListIcon from '@mui/icons-material/FilterList';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsIcon from '@mui/icons-material/Directions';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import Header from '../components/Header';
import FilterChips, { FilterOption } from '../components/FilterChips';
import SaleCard from '../components/SaleCard';
import FilterDrawer from '../components/FilterDrawer';
import { useSales } from '../hooks/useSales';
import { useApp } from '../context/AppContext';
import { Sale, MvpCity, MVP_CITIES, CITY_COORDINATES, SaleType, SourceWebsite } from '../types';

// v1 map style constants
const PIN_COLOR = '#F67B02';
const MAP_BG   = '#E0E8E0';

const MAP_STYLES = `
  .leaflet-container { background: ${MAP_BG} !important; }
  .leaflet-container::before {
    content: '';
    position: absolute; inset: 0; pointer-events: none; z-index: 0;
    background-image:
      linear-gradient(rgba(255,255,255,0.65) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.65) 1px, transparent 1px),
      linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px);
    background-size: 72px 72px, 72px 72px, 18px 18px, 18px 18px;
  }
  @keyframes pinDrop {
    from { opacity: 0; transform: translateY(-16px) scale(0.8); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes userPulse {
    0%   { transform: scale(1); opacity: 1; }
    100% { transform: scale(3); opacity: 0; }
  }
  .yard-pin { animation: pinDrop 0.38s cubic-bezier(0.34,1.56,0.64,1) both; }
  .yard-user-dot::before {
    content: '';
    position: absolute; inset: 4px; border-radius: 50%;
    background: #2563EB;
    box-shadow: 0 0 0 3px white, 0 2px 8px rgba(37,99,235,0.45);
  }
  .yard-user-dot::after {
    content: '';
    position: absolute; inset: 0; border-radius: 50%;
    background: rgba(37,99,235,0.15);
    animation: userPulse 2.2s infinite;
  }
`;

const makeBubbleIcon = (label: string, delay = 0) =>
  L.divIcon({
    className: '',
    iconSize: [0, 0],
    iconAnchor: [0, 28],
    html: `<div class="yard-pin" style="animation-delay:${delay}ms;background:white;border:2px solid ${PIN_COLOR};border-radius:10px 10px 10px 2px;padding:5px 9px;font-size:11px;font-weight:800;color:#111318;white-space:nowrap;box-shadow:0 2px 10px rgba(0,0,0,0.09);display:inline-flex;align-items:center;gap:4px;font-family:system-ui,sans-serif;"><span style="width:6px;height:6px;border-radius:50%;background:${PIN_COLOR};flex-shrink:0;display:inline-block"></span>${label}</div>`,
  });

const userDotIcon = () =>
  L.divIcon({
    className: '',
    html: `<div class="yard-user-dot" style="position:relative;width:20px;height:20px;"></div>`,
    iconAnchor: [10, 10],
  });

const inferSaleType = (title: string | null, sourceWebsite: string | null): SaleType => {
  const titleLower = title?.toLowerCase() || '';
  const sourceLower = sourceWebsite?.toLowerCase() || '';
  
  if (titleLower.includes('estate') || sourceLower.includes('estate')) {
    return 'estate_sale';
  }
  if (titleLower.includes('multi-family') || titleLower.includes('multi family')) {
    return 'multi_family';
  }
  if (titleLower.includes('moving')) {
    return 'moving_sale';
  }
  if (titleLower.includes('fundraiser')) {
    return 'fundraiser';
  }
  
  return 'yard_sale';
};

const extractCoordinates = (locationData: string | null): [number, number] | null => {
  if (!locationData) return null;
  
  if (typeof locationData === 'string' && locationData.startsWith('0101000020')) {
    try {
      const lonHex = locationData.substring(18, 34);
      const latHex = locationData.substring(34, 50);

      const getDoubleFromHex = (hex: string) => {
        const bytes = new Uint8Array(8);
        for (let i = 0; i < 8; i++) {
          bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16);
        }
        return new DataView(bytes.buffer).getFloat64(0, true);
      };

      const lon = getDoubleFromHex(lonHex);
      const lat = getDoubleFromHex(latHex);
      return [lat, lon];
    } catch (e) {
      return null;
    }
  }
  
  if (typeof locationData === 'string') {
    const match = locationData.match(/POINT\(([^ ]+) ([^)]+)\)/);
    if (match) {
      const lon = parseFloat(match[1]);
      const lat = parseFloat(match[2]);
      if (!isNaN(lat) && !isNaN(lon)) return [lat, lon];
    }
  }
  
  return null;
};

const MapCenterer: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, map.getZoom());
    setTimeout(() => map.invalidateSize(), 250);
  }, [center, map]);
  
  return null;
};

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { selectedCity, setSelectedCity, filterOption, setFilterOption, favorites, toggleFavorite } = useApp();
  const [searchQuery, setSearchQuery] = useState('');
  // Bottom sheet touch handling
  const sheetRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const touchCurrentY = useRef(0);
  const isDragging = useRef(false);
  
  const MIN_SHEET_HEIGHT = 120; // Collapsed height
  const HEADER_SEARCH_HEIGHT = 140; // Height of header + search + city selector
  const MAX_SHEET_HEIGHT = window.innerHeight - HEADER_SEARCH_HEIGHT - 60; // Stop below search bar
  
  const [sheetHeight, setSheetHeight] = useState(MIN_SHEET_HEIGHT);
  
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = true;
  };
  
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current) return;
    
    touchCurrentY.current = e.touches[0].clientY;
    const deltaY = touchStartY.current - touchCurrentY.current;
    
    // Calculate new height based on drag
    let newHeight = MIN_SHEET_HEIGHT + deltaY;
    newHeight = Math.max(MIN_SHEET_HEIGHT, Math.min(newHeight, MAX_SHEET_HEIGHT));
    
    setSheetHeight(newHeight);
  };
  
  const handleTouchEnd = () => {
    isDragging.current = false;
    
    // Snap to nearest position
    const midPoint = (MIN_SHEET_HEIGHT + MAX_SHEET_HEIGHT) / 2;
    
    if (sheetHeight > midPoint) {
      // Animate to fullscreen
      setSheetHeight(MAX_SHEET_HEIGHT);
    } else {
      // Animate to collapsed
      setSheetHeight(MIN_SHEET_HEIGHT);
    }
  };

  // Determine if sheet is expanded (more than 50% open)
  const isSheetExpanded = sheetHeight > (MIN_SHEET_HEIGHT + MAX_SHEET_HEIGHT) / 2;

  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState<Sale | null>(null);
  const [activeFilters, setActiveFilters] = useState<{
    saleType: SaleType | 'all';
    dateRange: 'all' | 'this_weekend' | 'this_week' | 'upcoming';
    sourceWebsite: SourceWebsite | 'all';
  }>({
    saleType: 'all',
    dateRange: 'all',
    sourceWebsite: 'all',
  });

  const { data: sales, isLoading, error } = useSales({ city: selectedCity });
  
  const cityCoords: [number, number] = [
    CITY_COORDINATES[selectedCity].lat,
    CITY_COORDINATES[selectedCity].lng
  ];

  const filteredSales = useMemo(() => {
    if (!sales) return [];

    let filtered = [...sales];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (sale) =>
          sale.title?.toLowerCase().includes(query) ||
          sale.address?.toLowerCase().includes(query) ||
          sale.description?.toLowerCase().includes(query)
      );
    }

    // Apply filter chips
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    switch (filterOption) {
      case 'this_weekend': {
        const dayOfWeek = today.getDay();
        const saturday = new Date(today);
        saturday.setDate(today.getDate() + (6 - dayOfWeek));
        const sunday = new Date(saturday);
        sunday.setDate(saturday.getDate() + 1);
        
        filtered = filtered.filter((sale) => {
          const saleStart = new Date(sale.start_date);
          return saleStart >= saturday && saleStart <= sunday;
        });
        break;
      }
      case 'estate_sale':
        filtered = filtered.filter((sale) => {
          const saleType = inferSaleType(sale.title, sale.source_website);
          return saleType === 'estate_sale';
        });
        break;
      case 'moving_sale':
        filtered = filtered.filter((sale) => {
          const saleType = inferSaleType(sale.title, sale.source_website);
          return saleType === 'moving_sale';
        });
        break;
      case 'multi_family':
        filtered = filtered.filter((sale) => {
          const saleType = inferSaleType(sale.title, sale.source_website);
          return saleType === 'multi_family';
        });
        break;
    }

    // Apply source website filter
    if (activeFilters.sourceWebsite !== 'all') {
      filtered = filtered.filter((sale) => sale.source_website === activeFilters.sourceWebsite);
    }

    return filtered;
  }, [sales, searchQuery, filterOption, activeFilters.sourceWebsite]);

  const salesWithLocation = filteredSales.filter(sale => extractCoordinates(sale.location));

  const handleCityChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCity(event.target.value as MvpCity);
  };

  const handleMarkerClick = (sale: Sale) => {
    setSelectedSale(sale);
    // Expand sheet to show the selected sale
    setSheetHeight(MAX_SHEET_HEIGHT);
  };

  const handleViewDetails = () => {
    if (selectedSale) {
      navigate(`/sale/${selectedSale.id}`);
    }
  };

  const handleClearSelection = () => {
    setSelectedSale(null);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const formatTime = (timeStr: string | null) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleFilterApply = (filters: { saleType: SaleType | 'all'; dateRange: 'all' | 'this_weekend' | 'this_week' | 'upcoming'; sourceWebsite: SourceWebsite | 'all' }) => {
    setActiveFilters(filters);
    setFilterDrawerOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: 'background.default', overflow: 'hidden' }}>
      {/* Header */}
      <Header />

      {/* Floating Search Bar */}
      <Box sx={{ 
        position: 'absolute', 
        top: 56, 
        left: 16, 
        right: 16, 
        zIndex: 1000 
      }}>
        <Paper 
          elevation={3} 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            px: 2,
            borderRadius: 2,
          }}
        >
          <TextField
            fullWidth
            placeholder="Search sales..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            variant="standard"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              disableUnderline: true,
            }}
            sx={{ 
              '& .MuiInputBase-input': {
                py: 1,
              }
            }}
          />
        </Paper>
        
        {/* City Selector */}
        <Paper 
          elevation={2}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            px: 2,
            py: 1,
            mt: 1,
            borderRadius: 2,
          }}
        >
          <LocationOnIcon sx={{ color: 'primary.main', fontSize: 20, mr: 1 }} />
          <select
            value={selectedCity}
            onChange={handleCityChange}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '0.95rem',
              fontWeight: 600,
              color: '#1A1A1A',
              cursor: 'pointer',
              outline: 'none',
              flex: 1,
            }}
          >
            {MVP_CITIES.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
        </Paper>
      </Box>

      {/* Map */}
      <style>{MAP_STYLES}</style>
      <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
        <MapContainer
          center={cityCoords}
          zoom={11}
          style={{ height: '100%', width: '100%' }}
          zoomControl={false}
        >
          {/* Labels-only tile layer — city/neighborhood names, no street detail */}
          <TileLayer
            attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png"
          />
          <MapCenterer center={cityCoords} />

          {/* User location dot */}
          <Marker position={cityCoords} icon={userDotIcon()} />

          {salesWithLocation.map((sale, i) => {
            const coords = extractCoordinates(sale.location);
            if (!coords) return null;
            const type = inferSaleType(sale.title, sale.source_website);
            const label = type === 'estate_sale' ? 'Estate Sale'
              : type === 'moving_sale' ? 'Moving Sale'
              : type === 'multi_family' ? 'Multi-Family'
              : 'Yard Sale';
            return (
              <Marker
                key={sale.id}
                position={coords}
                icon={makeBubbleIcon(label, i * 40)}
                eventHandlers={{ click: () => handleMarkerClick(sale) }}
              />
            );
          })}
        </MapContainer>
      </Box>

      {/* Filter Chips - Above Bottom Sheet */}
      <Box sx={{ 
        position: 'absolute', 
        bottom: sheetHeight + 10, 
        left: 0, 
        right: 0,
        zIndex: 900,
        px: 2,
        transition: 'bottom 0.2s ease',
      }}>
        <FilterChips
          selectedFilter={filterOption}
          onFilterChange={setFilterOption}
        />
      </Box>

      {/* FAB for Filters */}
      <Fab 
        color="primary" 
        onClick={() => setFilterDrawerOpen(true)}
        sx={{ 
          position: 'absolute', 
          right: 16, 
          bottom: sheetHeight + 20, 
          zIndex: 1000,
          transition: 'bottom 0.2s ease',
        }}
      >
        <FilterListIcon />
      </Fab>

      {/* FAB for My Location */}
      <Fab 
        size="small"
        sx={{ 
          position: 'absolute', 
          right: 16, 
          bottom: sheetHeight + 90, 
          zIndex: 1000,
          bgcolor: 'white',
          color: 'primary.main',
          transition: 'bottom 0.2s ease',
        }}
      >
        <MyLocationIcon />
      </Fab>

      {/* Swipe-Up Bottom Sheet */}
      <Paper 
        ref={sheetRef}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        sx={{ 
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: sheetHeight,
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: isDragging.current ? 'none' : 'height 0.3s ease',
          zIndex: 950,
        }}
        elevation={5}
      >
        {/* Handle Bar */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            pt: 1.5,
            pb: 1,
            cursor: 'grab',
            bgcolor: 'background.paper',
          }}
          onClick={() => setSheetHeight(isSheetExpanded ? MIN_SHEET_HEIGHT : MAX_SHEET_HEIGHT)}
        >
          <Box sx={{ width: 40, height: 4, bgcolor: 'grey.300', borderRadius: 2 }} />
        </Box>
        
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 2,
          pb: 1,
          bgcolor: 'background.paper',
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {selectedSale ? selectedSale.title : `${filteredSales.length} sales nearby`}
          </Typography>
          {selectedSale ? (
            <IconButton size="small" onClick={handleClearSelection}>
              <Typography variant="body2" color="primary">Show all</Typography>
            </IconButton>
          ) : null}
        </Box>

        {/* Content - Either selected sale details or list */}
        <Box sx={{ 
          flex: 1,
          overflow: 'auto',
          px: 2, 
          pb: 2,
          bgcolor: 'background.paper',
        }}>
          {selectedSale ? (
            /* Selected Sale Details */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {selectedSale.address}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarTodayIcon sx={{ fontSize: 16, color: 'primary.main', mr: 0.5 }} />
                  <Typography variant="body2">
                    {formatDate(selectedSale.start_date)}
                    {selectedSale.end_date && selectedSale.end_date !== selectedSale.start_date && 
                      ` - ${formatDate(selectedSale.end_date)}`}
                  </Typography>
                </Box>
                
                {selectedSale.start_time && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccessTimeIcon sx={{ fontSize: 16, color: 'primary.main', mr: 0.5 }} />
                    <Typography variant="body2">
                      {formatTime(selectedSale.start_time)}
                      {selectedSale.end_time && ` - ${formatTime(selectedSale.end_time)}`}
                    </Typography>
                  </Box>
                )}
              </Box>

              {selectedSale.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {selectedSale.description?.substring(0, 150)}
                  {selectedSale.description && selectedSale.description.length > 150 ? '...' : ''}
                </Typography>
              )}
              
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button 
                  variant="contained" 
                  fullWidth 
                  onClick={handleViewDetails}
                  endIcon={<ArrowForwardIcon />}
                >
                  View Details
                </Button>
              </Box>
            </Box>
          ) : (
            /* Sales List */
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4, width: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : filteredSales.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, width: '100%' }}>
                  <Typography color="text.secondary">No sales found</Typography>
                </Box>
              ) : (
                filteredSales.map((sale) => (
                  <SaleCard 
                    key={sale.id}
                    sale={sale}
                    isFavorite={favorites.includes(sale.id)}
                    onFavoriteToggle={toggleFavorite}
                  />
                ))
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Filter Drawer */}
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        onApply={handleFilterApply}
        activeFilters={activeFilters}
      />
    </Box>
  );
};

export default HomeScreen;
